<template>
  <div class="omnicanvas-wrapper" @click="deselectActiveItem">
    <div @click.stop="$emit('canvas-click', $event)">
      <canvas
        ref="canvas"
        class="max-w-full"
        :class="{
          shadow,
        }"
        @drop="$emit('drop', $event)"
        @dragover="$emit('dragover', $event)"
      />
    </div>
  </div>
</template>

<script>
import {Vector} from '@teemill/common/classes';
import merge from 'deepmerge';
import OmniCanvas from '../omnicanvas';

export default {
  name: 'OmniCanvas',

  props: {
    name: {
      type: String,
      required: true,
    },

    width: {
      type: Number,
      default: 500,
    },

    height: {
      type: Number,
      default: 500,
    },

    /**
     * If true, users will be able to interact with the canvas via mouse and keyboard
     * events
     */
    evented: {
      type: Boolean,
      default: true,
    },

    onElementSelected: {
      type: Function,
    },

    onElementDeselected: {
      type: Function,
    },

    onElementActivated: {
      type: Function,
    },

    onElementAdded: {
      type: Function,
    },

    onElementDeleted: {
      type: Function,
    },

    layers: {
      type: Array,
      default: () => [{tag: 'default', sortorder: 1}],
    },

    minZoom: {
      type: Number,
    },

    maxZoom: {
      type: Number,
    },

    global: {
      type: Boolean,
    },

    fade: {
      type: String,
      default: 'block',
    },

    customOptions: {
      type: Object,
      default: () => ({}),
    },

    withClasses: {
      type: Object,
    },

    shadow: Boolean,
  },

  data() {
    return {
      canvas: null,
      omniCanvas: null,
      eventForwarders: ['rendered'],
    };
  },

  computed: {
    canvasId() {
      return `omnicanvas_${this.name}`;
    },

    showBackground() {
      return this.customOptions?.background?.imageEnabled;
    },
  },

  watch: {
    width() {
      this.omniCanvas.updateDimensions(this.width, this.height);
    },
    height() {
      this.omniCanvas.updateDimensions(this.width, this.height);
    },
  },

  mounted() {
    this.canvas = this.$refs.canvas;

    this.addCanvasEventListeners();

    this.omniCanvas = new OmniCanvas(
      this.canvas,
      merge(
        {
          stage: {
            width: this.width,
            height: this.height,
            evented: this.evented,
            globalControlsEnabled: this.global,
            minZoom: this.minZoom,
            maxZoom: this.maxZoom,
            fadeMethod: this.fade,
            layers: this.layers,
            activeLayer: this.layers.length ? this.layers[0].tag : null,
          },
          onElementSelected: this.onElementSelected,
          onElementDeselected: this.onElementDeselected,
          onElementActivated: this.onElementActivated,
          onElementAdded: this.onElementAdded,
          onElementDeleted: this.onElementDeleted,
          globalTranslation: new Vector(0, 0),
        },
        this.customOptions
      ),
      this.withClasses
    );
  },

  beforeUnmount() {
    this.canvas.removeEventListener(
      'canvasInitialised',
      this.onCanvasInitialised
    );

    this.eventForwarders.forEach(event => {
      this.canvas.removeEventListener(event, this.forwardEvent);
    });

    this.omniCanvas.destroy();

    this.canvas = null;
    this.omniCanvas = null;

    this.$emit('destroyed');
  },

  methods: {
    addCanvasEventListeners() {
      this.canvas.addEventListener(
        'canvasInitialised',
        this.onCanvasInitialised
      );

      this.eventForwarders.forEach(event => {
        this.canvas.addEventListener(event, this.forwardEvent);
      });
    },

    onCanvasInitialised(e) {
      this.$emit('initialised', e.detail.canvas, e);
    },

    forwardEvent(e) {
      this.$emit(e.type, e);
    },

    deselectActiveItem() {
      const deselectedElement = this.omniCanvas.deselectActiveElement();
      this.omniCanvas.render();
      this.omniCanvas.onElementDeselected(deselectedElement);
    },
  },
};
</script>

<style scoped lang="scss">
.studio-container .omnicanvas-bg-img-container {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
}

.omnicanvas-wrapper {
  canvas {
    z-index: 5;

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
      border: 0;
    }
  }
}
</style>

<style>
textarea.omnicanvas-textEditor {
  position: absolute;
  font-size: 16px;
  height: 1px;
  width: 1px;
  z-index: -99;
  top: 0;
  opacity: 0;
}
</style>
